import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Enhanced proctoring hook. Detects:
 *  1. Face missing / drifting to edge         → "face_missing" / "attention_drift"
 *  2. Eyes looking down (phone/paper)         → "looking_down"  (canvas brightness)
 *  3. Tab switch (visibilitychange)           → "tab_switch"    (1.5s grace)
 *  4. Window blur / alt-tab                   → "window_blur"   (1.5s grace)
 *  5. Multiple people in frame                → "multiple_faces" (immediate)
 */
export default function useEyeTracking(videoRef, { enabled = true, onEvent } = {}) {
  const [status, setStatus]     = useState("idle");
  const [cheatCount, setCheatCount] = useState(0);

  const awayStartRef        = useRef(null);
  const lowBrightStartRef   = useRef(null);
  const lastFiredRef        = useRef({});   // per-type last-fired timestamp
  const intervalRef         = useRef(null);
  const detectorRef         = useRef(null);
  const canvasRef           = useRef(null);
  const pendingEventsRef    = useRef([]);   // accumulate between resets
  const blurTimerRef        = useRef(null);
  const blurStartRef        = useRef(null);
  const onEventRef          = useRef(onEvent);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  // ── Per-type cooldown fire ─────────────────────────────────────────────────
  const fireEvent = useCallback((type, durationSec = 0) => {
    const now = Date.now();
    const cooldown = type === "multiple_faces" ? 8000 : 5000;
    if (now - (lastFiredRef.current[type] || 0) < cooldown) return;
    lastFiredRef.current[type] = now;

    const evt = { type, at: new Date().toISOString(), duration_sec: Math.round(durationSec * 10) / 10 };
    pendingEventsRef.current.push(evt);
    setCheatCount((c) => c + 1);
    onEventRef.current?.(evt);
  }, []);

  // ── Initialise detector + offscreen canvas ─────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    if ("FaceDetector" in window) {
      // eslint-disable-next-line no-undef
      detectorRef.current = new FaceDetector({ fastMode: true, maxDetectedFaces: 2 });
    }
    const c = document.createElement("canvas");
    c.width = 64; c.height = 24;
    canvasRef.current = c;
  }, [enabled]);

  // ── Eye-region brightness (looking-down detection) ─────────────────────────
  const getEyeBrightness = useCallback((video, face) => {
    const canvas = canvasRef.current;
    if (!canvas || !face) return null;
    const f = face.boundingBox;
    if (!f || f.width <= 0 || f.height <= 0) return null;
    try {
      const ctx = canvas.getContext("2d");
      // Crop top 38% of face bounding box — where the eyes live
      ctx.drawImage(video, f.x, f.y, f.width, f.height * 0.38, 0, 0, 64, 24);
      const px = ctx.getImageData(0, 0, 64, 24).data;
      let sum = 0;
      for (let i = 0; i < px.length; i += 4) {
        sum += px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114;
      }
      return sum / (px.length / 4); // 0–255
    } catch { return null; }
  }, []);

  // ── Tab-switch + window-blur listeners ────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const startBlurTimer = (type) => {
      clearTimeout(blurTimerRef.current);
      blurStartRef.current = Date.now();
      blurTimerRef.current = setTimeout(() => {
        const dur = (Date.now() - blurStartRef.current) / 1000;
        fireEvent(type, dur);
      }, 1500);
    };

    const cancelBlurTimer = () => {
      clearTimeout(blurTimerRef.current);
      blurStartRef.current = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") startBlurTimer("tab_switch");
      else cancelBlurTimer();
    };

    const onBlur  = () => { if (document.visibilityState === "visible") startBlurTimer("window_blur"); };
    const onFocus = () => cancelBlurTimer();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      clearTimeout(blurTimerRef.current);
    };
  }, [enabled, fireEvent]);

  // ── Main face-tracking loop (500ms) ───────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const video = videoRef.current;
    if (!video) return;

    // Brightness below this level = eyelids covering iris = looking down
    const EYE_BRIGHTNESS_THRESHOLD = 50;

    const tick = async () => {
      if (!video.videoWidth) return;
      try {
        if (!detectorRef.current) {
          // Non-Chromium fallback: only tab visibility
          if (document.visibilityState === "hidden") setStatus("warning");
          else setStatus("ok");
          return;
        }

        const faces = await detectorRef.current.detect(video);
        const now = Date.now();

        // ── Multiple faces — fire immediately ─────────────────────────────
        if (faces && faces.length > 1) {
          setStatus("warning");
          fireEvent("multiple_faces", 0);
          awayStartRef.current = null;
          lowBrightStartRef.current = null;
          return;
        }

        // ── No face ───────────────────────────────────────────────────────
        if (!faces || faces.length === 0) {
          if (!awayStartRef.current) awayStartRef.current = now;
          const dur = (now - awayStartRef.current) / 1000;
          setStatus("lost");
          if (dur > 2) fireEvent("face_missing", dur);
          lowBrightStartRef.current = null;
          return;
        }

        // ── Single face detected ───────────────────────────────────────────
        const f = faces[0].boundingBox;
        const cx = (f.x + f.width  / 2) / video.videoWidth;
        const cy = (f.y + f.height / 2) / video.videoHeight;
        const outsideZone = cx < 0.2 || cx > 0.8 || cy < 0.15 || cy > 0.85;

        // Eye brightness check
        const brightness = getEyeBrightness(video, faces[0]);
        const eyesDown = brightness !== null && brightness < EYE_BRIGHTNESS_THRESHOLD;

        if (outsideZone || eyesDown) {
          if (!awayStartRef.current) awayStartRef.current = now;
          const dur = (now - awayStartRef.current) / 1000;

          if (eyesDown) {
            if (!lowBrightStartRef.current) lowBrightStartRef.current = now;
            const downDur = (now - lowBrightStartRef.current) / 1000;
            setStatus("looking_down");
            if (downDur > 1.5) fireEvent("looking_down", downDur);
          } else {
            lowBrightStartRef.current = null;
            setStatus("warning");
            if (dur > 2) fireEvent("attention_drift", dur);
          }
        } else {
          awayStartRef.current     = null;
          lowBrightStartRef.current = null;
          setStatus("ok");
        }
      } catch { /* ignore per-frame errors */ }
    };

    intervalRef.current = setInterval(tick, 500);
    return () => clearInterval(intervalRef.current);
  }, [enabled, videoRef, getEyeBrightness, fireEvent]);

  // ── Reset — returns accumulated events and clears counter ─────────────────
  const resetEvents = useCallback(() => {
    const evts = [...pendingEventsRef.current];
    pendingEventsRef.current = [];
    setCheatCount(0);
    return evts;
  }, []);

  return { status, cheatCount, resetEvents };
}
